// frontend/src/pages/dashboard/TaskComponents/TaskTableRowSkeleton.js

import React from "react";
import { TableRow, TableCell, Box, Skeleton, useTheme, useMediaQuery } from "@mui/material";

const TaskTableRowSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // Show 5 skeleton rows
  const skeletonRows = 5;

  return (
    <>
      {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
        <TableRow
          key={rowIndex}
          sx={{
            '&:last-child td': { borderBottom: 0 },
            '& .MuiTableCell-root': {
              boxSizing: 'border-box',
              py: 1.5,
              px: 1
            }
          }}
        >
          {/* Index Column */}
          <TableCell
            align="center"
            sx={{
              width: { xs: 40, sm: 50, md: 60 },
              minWidth: { xs: 40, sm: 50, md: 60 },
              maxWidth: { xs: 40, sm: 50, md: 60 },
            }}
          >
            <Skeleton
              variant="text"
              width={30}
              sx={{ fontSize: "0.875rem", mx: "auto" }}
            />
          </TableCell>

          {/* Task Title Column */}
          <TableCell
            sx={{
              minWidth: { xs: 150, md: 200 },
              width: '100%',
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Skeleton
                variant="text"
                width="80%"
                height={28}
                sx={{ fontSize: "1rem" }}
              />
              <Skeleton
                variant="text"
                width="40%"
                height={16}
                sx={{ fontSize: "0.75rem" }}
              />
            </Box>
          </TableCell>

          {/* Project Column */}
          {!isSmallDesktop && (
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 150,
                width: 120,
              }}
            >
              <Skeleton
                variant="text"
                width="70%"
                height={24}
                sx={{ fontSize: "0.875rem" }}
              />
            </TableCell>
          )}

          {/* Sprint Column */}
          {!isSmallDesktop && (
            <TableCell
              sx={{
                minWidth: 100,
                maxWidth: 120,
                width: 100,
              }}
            >
              <Skeleton
                variant="rectangular"
                width={80}
                height={22}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
          )}

          {/* Due Date Column */}
          {!isMobile && (
            <TableCell
              sx={{
                minWidth: 110,
                maxWidth: 130,
                width: 110,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={20}
                  sx={{ fontSize: "0.875rem" }}
                />
                <Skeleton
                  variant="text"
                  width="40%"
                  height={14}
                  sx={{ fontSize: "0.75rem" }}
                />
              </Box>
            </TableCell>
          )}

          {/* Tracked Time Column */}
          {!isSmallDesktop && (
            <TableCell
              sx={{
                minWidth: 90,
                maxWidth: 110,
                width: 90,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton
                  variant="text"
                  width={50}
                  height={20}
                  sx={{ fontSize: "0.875rem" }}
                />
                <Skeleton
                  variant="circular"
                  width={8}
                  height={8}
                />
              </Box>
            </TableCell>
          )}

          {/* Priority Column */}
          <TableCell
            sx={{
              minWidth: 80,
              maxWidth: 100,
              width: 80,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={60}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </TableCell>

          {/* Status Column */}
          <TableCell
            sx={{
              minWidth: 90,
              maxWidth: 110,
              width: 90,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={70}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </TableCell>

          {/* Assigned Column */}
          {!isSmallDesktop && (
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 150,
                width: 120,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                />
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                />
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                />
              </Box>
            </TableCell>
          )}

          {/* Actions Column */}
          <TableCell
            align="right"
            sx={{
              minWidth: isSmallDesktop ? 100 : 160,
              maxWidth: isSmallDesktop ? 100 : 180,
              width: isSmallDesktop ? 100 : 160,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: { xs: 0.25, md: 0.5 },
              }}
            >
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="circular" width={28} height={28} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

export default TaskTableRowSkeleton;